import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { Box, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

// Components
import { NoneAvailableMessage, ProductIndicatorBadge, WizardCheckbox } from 'common';

// Context
import { SequencingWizardContext } from '../SequencingWizardContext';

const notEmpty = R.complement(R.isEmpty);

const Header = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(3, 3, 2),
}));

const ProductCheckboxList = props => {
  const { fetchProducts, onChange, products, productsLoading, title } = props;
  const {
    data: { currentAffiliate, inventoryOwners = [], loadProducts, products: selected = [] },
    setData,
  } = useContext(SequencingWizardContext);
  const { dataCompanyId, customerIdName } = currentAffiliate;

  const theme = useTheme();
  const boxProps = {
    alignItems: 'center',
    borderBottom: 1,
    color: 'graphic.light',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 3),
    width: '100%',
  };

  const handleChange = (checked, product) => {
    setData(
      R.evolve({
        products: checked ? R.append(product) : R.reject(R.equals(product)),
      })
    );
    setData(R.assoc('locations', []));
    setData(
      R.evolve({
        loadLocations: R.T,
      })
    );
  };

  const handleSelectAllChange = (_, checked) => {
    setData(R.assoc('products', checked ? products : []));
    setData(R.assoc('locations', []));
    setData(
      R.evolve({
        loadLocations: R.T,
      })
    );
  };

  useEffect(() => {
    if (loadProducts) {
      fetchProducts({
        companyCustomerIdName: customerIdName,
        dataCompanyId,
        inventoryOwners: R.pluck('companyCustomerIdName', inventoryOwners),
      });
      setData(
        R.evolve({
          loadProducts: R.F,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCompanyId, inventoryOwners, customerIdName]);

  useEffect(() => {
    onChange(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const noSelectableProductsMessage = (
    <Typography variant="body2">
      There are no selectable products available for your Inventory Owner selection.
    </Typography>
  );

  return (
    <>
      <Header color="textPrimary" variant="body1">
        {title}
      </Header>
      {R.isEmpty(products) && !loadProducts && !productsLoading ? (
        <NoneAvailableMessage message={noSelectableProductsMessage} />
      ) : (
        <>
          <Box {...boxProps}>
            <WizardCheckbox
              checked={R.and(
                notEmpty(selected),
                R.equals(R.sortBy(R.prop('id'), products), R.sortBy(R.prop('id'), selected))
              )}
              name="Select All Products"
              onChange={handleSelectAllChange}
            />
          </Box>

          {R.map(
            product => (
              <Box {...boxProps} key={product.id}>
                <WizardCheckbox
                  checked={R.includes(product, selected)}
                  name={product.name}
                  onChange={(_, checked) => handleChange(checked, product)}
                />
                <ProductIndicatorBadge proprietary={product.proprietary} />
              </Box>
            ),
            products
          )}
        </>
      )}
    </>
  );
};

ProductCheckboxList.propTypes = {
  fetchProducts: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      proprietary: PropTypes.bool,
    })
  ).isRequired,
  productsLoading: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
};

export default ProductCheckboxList;
